// optimisticNow — what the Today screen shows as "on the clock" while a
// clock_in or clock_out is still in the queue.
//
// Stored in localStorage as `fw_field_now` = { key, now } where `key` is the
// queued op's key and `now` is the LiveCard shape (or null after a queued
// clock_out). A reload with no signal reads it back and the clock keeps
// running from the local `at`.
//
// resolveNow() is the reconciliation rule and it is deliberately small:
//
//   - the op is still pending      → the optimistic state wins (the server
//                                    has not heard about it yet)
//   - the op failed                → drop it; the server's word stands
//   - the op was sent, and the     → the server's `now` is the truth; the
//     data on screen is fresh        optimistic entry is cleared
//   - the op was sent, but the     → keep showing it; the cached bundle
//     data on screen is a cache      predates the op and would lie

export const NOW_KEY = 'fw_field_now';

function storage(override) {
  if (override) return override;
  try { return window.localStorage; } catch (_) { return null; }
}

export function readOptimisticNow(store) {
  const s = storage(store);
  if (!s) return null;
  try {
    const raw = s.getItem(NOW_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && parsed.key ? parsed : null;
  } catch (_) { return null; }
}

export function writeOptimisticNow(value, store) {
  const s = storage(store);
  if (!s) return;
  try {
    if (!value) s.removeItem(NOW_KEY);
    else s.setItem(NOW_KEY, JSON.stringify(value));
  } catch (_) { /* */ }
}

export function clearOptimisticNow(store) { writeOptimisticNow(null, store); }

/**
 * @param {object|null} serverNow  the `now` from the bundle on screen
 * @param {object|null} optimistic the stored { key, now }
 * @param {object}      queue      { has(key), get(key) }
 * @param {boolean}     fresh      true when the bundle came from the server, not the cache
 * @returns {{ now: object|null, clear: boolean }}
 */
export function resolveNow(serverNow, optimistic, queue, fresh) {
  if (!optimistic) return { now: serverNow || null, clear: false };
  if (queue.has(optimistic.key)) {
    return { now: optimistic.now ? { ...optimistic.now, queued: true } : null, clear: false };
  }
  const rec = queue.get(optimistic.key);
  if (rec && rec.status === 'failed') return { now: serverNow || null, clear: true };
  if (fresh) return { now: serverNow || null, clear: true };
  return { now: optimistic.now ? { ...optimistic.now, queued: true } : null, clear: false };
}
